import React, { useState } from 'react';
import { Upload, X, Camera, ChevronLeft, Package, DollarSign, Tag, MapPin, Store, User, Phone, Check } from 'lucide-react';

import { colorMap } from '../modules/colorMap';
import { categories } from '../modules/categories';

const batiments = ['Bât. A', 'Bât. B', 'Bât. C', 'Bât. D', 'Bât. E', 'Bât. F', 'Bât. G', 'Bât. H'];
const paliers = ['Palier 1', 'Palier 2', 'Palier 3', 'Palier 4'];

export default function CrouVenteForm() {
  const [formData, setFormData] = useState({
    productName: '',
    price: '',
    category: '',
    description: '',
    sellerName: '',
    shopName: '',
    phone: '',
    residence: 'ABOBO 1',
    batiment: '',
    palier: '',
    chambre: '',
  });

  const [images, setImages] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      url: URL.createObjectURL(file),
      file: file
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 5));
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.productName.trim()) newErrors.productName = 'Nom du produit requis';
    if (!formData.price || formData.price <= 0) newErrors.price = 'Prix valide requis';
    if (!formData.category) newErrors.category = 'Catégorie requise';
    if (!formData.sellerName.trim()) newErrors.sellerName = 'Votre nom est requis';
    if (!formData.phone.trim()) newErrors.phone = 'Numéro de téléphone requis';
    if (!formData.batiment) newErrors.batiment = 'Bâtiment requis';
    if (!formData.palier) newErrors.palier = 'Palier requis';
    if (!formData.chambre.trim()) newErrors.chambre = 'Numéro de chambre requis';
    if (images.length === 0) newErrors.images = 'Au moins une photo est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      console.log('Formulaire soumis:', formData, images);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Reset form
        setFormData({
          productName: '',
          price: '',
          category: '',
          description: '',
          sellerName: '',
          shopName: '',
          phone: '',
          residence: 'ABOBO 1',
          batiment: '',
          palier: '',
          chambre: '',
        });
        setImages([]);
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colorMap.bg.light }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: colorMap.bg.white, borderBottom: `1px solid ${colorMap.border.default}` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              className="p-2 rounded-lg transition-all duration-200"
              style={{ color: colorMap.text.dark }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colorMap.bg.light;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <ChevronLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: colorMap.primary.base }}>
                Vendre un produit
              </h1>
              <p className="text-sm" style={{ color: colorMap.text.secondary }}>
                Remplissez les informations ci-dessous
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Photos Section */}
          <div 
            className="p-6 rounded-xl"
            style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Camera size={20} style={{ color: colorMap.secondary.base }} />
              <h2 className="text-lg font-semibold" style={{ color: colorMap.text.primary }}>
                Photos du produit
              </h2>
              <span className="text-sm" style={{ color: colorMap.text.muted }}>
                (Maximum 5 photos)
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {images.map((image) => (
                <div key={image.id} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img 
                    src={image.url} 
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: colorMap.bg.white }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}

              {images.length < 5 && (
                <label 
                  className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
                  style={{ borderColor: colorMap.border.default }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colorMap.secondary.base;
                    e.currentTarget.style.backgroundColor = colorMap.bg.light;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colorMap.border.default;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Upload size={24} style={{ color: colorMap.text.muted }} />
                  <span className="text-xs mt-2 text-center px-2" style={{ color: colorMap.text.muted }}>
                    Ajouter
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {errors.images && (
              <p className="text-sm mt-2" style={{ color: '#ef4444' }}>
                {errors.images}
              </p>
            )}
          </div>

          {/* Product Info Section */}
          <div 
            className="p-6 rounded-xl space-y-5"
            style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Package size={20} style={{ color: colorMap.secondary.base }} />
              <h2 className="text-lg font-semibold" style={{ color: colorMap.text.primary }}>
                Informations du produit
              </h2>
            </div>

            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Nom du produit *
              </label>
              <input
                type="text"
                value={formData.productName}
                onChange={(e) => handleInputChange('productName', e.target.value)}
                placeholder="Ex: iPhone 13 Pro 128GB"
                className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                style={{
                  borderColor: errors.productName ? '#ef4444' : colorMap.border.default,
                  backgroundColor: colorMap.bg.white
                }}
                onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                onBlur={(e) => e.target.style.borderColor = errors.productName ? '#ef4444' : colorMap.border.default}
              />
              {errors.productName && (
                <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                  {errors.productName}
                </p>
              )}
            </div>

            {/* Price and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                  Prix (FCFA) *
                </label>
                <div className="relative">
                  <DollarSign size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="Ex: 350000"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none transition-all"
                    style={{
                      borderColor: errors.price ? '#ef4444' : colorMap.border.default,
                      backgroundColor: colorMap.bg.white
                    }}
                    onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                    onBlur={(e) => e.target.style.borderColor = errors.price ? '#ef4444' : colorMap.border.default}
                  />
                </div>
                {errors.price && (
                  <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                    {errors.price}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                  Catégorie *
                </label>
                <div className="relative">
                  <Tag size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none transition-all appearance-none"
                    style={{
                      borderColor: errors.category ? '#ef4444' : colorMap.border.default,
                      backgroundColor: colorMap.bg.white
                    }}
                    onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                    onBlur={(e) => e.target.style.borderColor = errors.category ? '#ef4444' : colorMap.border.default}
                  >
                    <option value="">Choisir une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                {errors.category && (
                  <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                    {errors.category}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Description (optionnel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Décrivez votre produit, son état, etc."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border outline-none transition-all resize-none"
                style={{
                  borderColor: colorMap.border.default,
                  backgroundColor: colorMap.bg.white
                }}
                onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                onBlur={(e) => e.target.style.borderColor = colorMap.border.default}
              />
            </div>
          </div>

          {/* Seller Info Section */}
          <div 
            className="p-6 rounded-xl space-y-5"
            style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <User size={20} style={{ color: colorMap.secondary.base }} />
              <h2 className="text-lg font-semibold" style={{ color: colorMap.text.primary }}>
                Vos informations
              </h2>
            </div>

            {/* Seller Name */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Votre nom *
              </label>
              <input
                type="text"
                value={formData.sellerName}
                onChange={(e) => handleInputChange('sellerName', e.target.value)}
                placeholder="Ex: Kouamé Jean"
                className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                style={{
                  borderColor: errors.sellerName ? '#ef4444' : colorMap.border.default,
                  backgroundColor: colorMap.bg.white
                }}
                onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                onBlur={(e) => e.target.style.borderColor = errors.sellerName ? '#ef4444' : colorMap.border.default}
              />
              {errors.sellerName && (
                <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                  {errors.sellerName}
                </p>
              )}
            </div>

            {/* Shop Name */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Nom de votre boutique (optionnel)
              </label>
              <div className="relative">
                <Store size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                <input
                  type="text"
                  value={formData.shopName}
                  onChange={(e) => handleInputChange('shopName', e.target.value)}
                  placeholder="Ex: TechStore Jean"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none transition-all"
                  style={{
                    borderColor: colorMap.border.default,
                    backgroundColor: colorMap.bg.white
                  }}
                  onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                  onBlur={(e) => e.target.style.borderColor = colorMap.border.default}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Numéro de téléphone *
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: colorMap.text.muted }} />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+225 07 08 09 10 11"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border outline-none transition-all"
                  style={{
                    borderColor: errors.phone ? '#ef4444' : colorMap.border.default,
                    backgroundColor: colorMap.bg.white
                  }}
                  onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                  onBlur={(e) => e.target.style.borderColor = errors.phone ? '#ef4444' : colorMap.border.default}
                />
              </div>
              {errors.phone && (
                <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                  {errors.phone}
                </p>
              )}
            </div>
          </div>

          {/* Location Section */}
          <div 
            className="p-6 rounded-xl space-y-5"
            style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={20} style={{ color: colorMap.secondary.base }} />
              <h2 className="text-lg font-semibold" style={{ color: colorMap.text.primary }}>
                Localisation
              </h2>
            </div>

            {/* Residence */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Résidence *
              </label>
              <select
                value={formData.residence}
                onChange={(e) => handleInputChange('residence', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                style={{
                  borderColor: colorMap.border.default,
                  backgroundColor: colorMap.bg.white
                }}
                onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                onBlur={(e) => e.target.style.borderColor = colorMap.border.default}
              >
                <option value="ABOBO 1">ABOBO 1</option>
                <option value="ABOBO 2">ABOBO 2</option>
              </select>
            </div>

            {/* Batiment and Palier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                  Bâtiment *
                </label>
                <select
                  value={formData.batiment}
                  onChange={(e) => handleInputChange('batiment', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                  style={{
                    borderColor: errors.batiment ? '#ef4444' : colorMap.border.default,
                    backgroundColor: colorMap.bg.white
                  }}
                  onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                  onBlur={(e) => e.target.style.borderColor = errors.batiment ? '#ef4444' : colorMap.border.default}
                >
                  <option value="">Choisir</option>
                  {batiments.map(bat => (
                    <option key={bat} value={bat}>{bat}</option>
                  ))}
                </select>
                {errors.batiment && (
                  <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                    {errors.batiment}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                  Palier *
                </label>
                <select
                  value={formData.palier}
                  onChange={(e) => handleInputChange('palier', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                  style={{
                    borderColor: errors.palier ? '#ef4444' : colorMap.border.default,
                    backgroundColor: colorMap.bg.white
                  }}
                  onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                  onBlur={(e) => e.target.style.borderColor = errors.palier ? '#ef4444' : colorMap.border.default}
                >
                  <option value="">Choisir</option>
                  {paliers.map(pal => (
                    <option key={pal} value={pal}>{pal}</option>
                  ))}
                </select>
                {errors.palier && (
                  <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                    {errors.palier}
                  </p>
                )}
              </div>
            </div>

            {/* Chambre */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colorMap.text.dark }}>
                Numéro de chambre *
              </label>
              <input
                type="text"
                value={formData.chambre}
                onChange={(e) => handleInputChange('chambre', e.target.value)}
                placeholder="Ex: 5"
                className="w-full px-4 py-3 rounded-lg border outline-none transition-all"
                style={{
                  borderColor: errors.chambre ? '#ef4444' : colorMap.border.default,
                  backgroundColor: colorMap.bg.white
                }}
                onFocus={(e) => e.target.style.borderColor = colorMap.secondary.base}
                onBlur={(e) => e.target.style.borderColor = errors.chambre ? '#ef4444' : colorMap.border.default}
              />
              {errors.chambre && (
                <p className="text-sm mt-1" style={{ color: '#ef4444' }}>
                  {errors.chambre}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 rounded-xl font-semibold transition-all duration-300"
            style={{
              backgroundColor: colorMap.secondary.base,
              color: colorMap.bg.white,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorMap.secondary.hover;
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorMap.secondary.base;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Publier mon produit
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccess && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div 
            className="max-w-sm w-full p-8 rounded-2xl text-center"
            style={{ backgroundColor: colorMap.bg.white }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: colorMap.primary.light }}
            >
              <Check size={32} style={{ color: colorMap.primary.base }} />
            </div>
            <h3 className="text-2xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
              Produit publié !
            </h3>
            <p style={{ color: colorMap.text.secondary }}>
              Votre produit est maintenant visible par tous les étudiants
            </p>
          </div>
        </div>
      )}
    </div>
  );
}